import { motion } from "framer-motion";
import {
  ArrowRight,
  Storefront,
  ForkKnife,
  Pizza,
  Coffee,
  BowlFood,
  ShoppingBag,
  IceCream,
  Sparkle,
} from "@phosphor-icons/react";

const APP_SCREENSHOT =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_local-ordering-hub/artifacts/chfqd1zh_IMG_1788.webp";

const QUICK_PICKS = [
  { label: "Takeaway", icon: ForkKnife, href: "#popular" },
  { label: "Pizza", icon: Pizza, href: "#popular" },
  { label: "Coffee", icon: Coffee, href: "#popular" },
  { label: "Indian", icon: BowlFood, href: "#popular" },
  { label: "Desserts", icon: IceCream, href: "#popular" },
  { label: "Groceries", icon: ShoppingBag, href: "#popular" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden bg-[#FAFAFA] pt-20 md:pt-24 pb-8 md:pb-10"
    >
      <div className="aura -top-24 -left-24 opacity-60" />
      <div className="aura -bottom-32 right-0 opacity-40" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={0}
            className="mb-4 flex flex-wrap items-center gap-3"
            data-testid="hero-overline"
          >
            <span className="overline text-zinc-500 inline-flex items-center gap-3">
              <span className="inline-block h-2 w-2 rounded-full brand-gradient" />
              <span>Used daily across the area</span>
            </span>
            <span
              data-testid="milestone-badge"
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full brand-gradient text-white shadow-sm shadow-[#FF1F8B]/25"
              style={{ letterSpacing: "0.01em" }}
            >
              <Sparkle size={11} weight="fill" />
              500K milestone
            </span>
          </motion.p>

          <motion.h1
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={1}
            className="font-display font-extrabold text-zinc-900 text-4xl md:text-5xl lg:text-6xl leading-[0.98] tracking-tighter"
            data-testid="hero-headline"
          >
            <span className="relative inline-block mr-3 md:mr-5">
              <span className="gradient-text-shimmer">500,000+</span>
              {/* Sparkles orbiting the number */}
              <motion.span
                aria-hidden
                className="absolute -top-2 -right-3 text-[#FF1F8B]"
                animate={{ opacity: [0, 1, 0], scale: [0.4, 1, 0.4], rotate: [0, 90, 180] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut", delay: 0 }}
              >
                <Sparkle size={18} weight="fill" />
              </motion.span>
              <motion.span
                aria-hidden
                className="absolute top-1 -left-4 text-[#C026D3]"
                animate={{ opacity: [0, 1, 0], scale: [0.3, 0.9, 0.3], rotate: [0, -60, -120] }}
                transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
              >
                <Sparkle size={14} weight="fill" />
              </motion.span>
              <motion.span
                aria-hidden
                className="absolute -bottom-1 right-6 text-[#FF4422]"
                animate={{ opacity: [0, 1, 0], scale: [0.5, 1.1, 0.5], rotate: [0, 45, 90] }}
                transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
              >
                <Sparkle size={12} weight="fill" />
              </motion.span>
              <motion.span
                aria-hidden
                className="absolute top-6 left-10 text-[#FF1F8B]"
                animate={{ opacity: [0, 0.9, 0], scale: [0.4, 0.8, 0.4], rotate: [0, 120, 240] }}
                transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut", delay: 1.8 }}
              >
                <Sparkle size={10} weight="fill" />
              </motion.span>
            </span>{" "}
            orders placed through <span className="gradient-text">My Local.</span>
          </motion.h1>

          <motion.p
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={2}
            className="mt-4 text-xl md:text-2xl font-semibold text-zinc-900 max-w-xl leading-snug tracking-tight"
            data-testid="hero-subline"
          >
            Across Strabane and the surrounding area.
          </motion.p>

          <motion.p
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={3}
            className="mt-3 text-base text-zinc-500 max-w-xl"
            data-testid="hero-subheading"
          >
            Your local spots, all in one place.
          </motion.p>

          {/* Quick-pick category tiles */}
          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={4}
            data-testid="hero-quick-picks"
            className="mt-8 grid grid-cols-3 sm:grid-cols-6 gap-2 md:gap-3"
          >
            {QUICK_PICKS.map((p) => (
              <a
                key={p.label}
                href={p.href}
                data-testid={`quickpick-${p.label.toLowerCase()}`}
                className="group flex flex-col items-center justify-center gap-2 rounded-2xl bg-white border border-zinc-200 hover:border-transparent hover:shadow-lg hover:shadow-[#FF1F8B]/15 hover:-translate-y-0.5 transition-all py-4 px-2"
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#C026D3]/10 via-[#FF1F8B]/10 to-[#FF4422]/10 text-[#FF1F8B] group-hover:brand-gradient group-hover:text-white transition-all">
                  <p.icon size={20} weight="duotone" />
                </span>
                <span className="text-xs md:text-sm font-semibold text-zinc-800">{p.label}</span>
              </a>
            ))}
          </motion.div>

          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={5}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <a
              href="https://www.mylocalapp.shop/"
              target="_blank"
              rel="noreferrer"
              data-testid="hero-order-now-btn"
              className="group inline-flex items-center gap-2 px-7 py-4 rounded-full brand-gradient text-white font-semibold shadow-lg shadow-[#FF1F8B]/30 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-[#FF1F8B]/40 transition-all"
            >
              Order local
              <ArrowRight size={18} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
            </a>
            <a
              href="#popular"
              data-testid="hero-browse-local-btn"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-zinc-900 font-semibold border border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50 transition-all"
            >
              <Storefront size={18} weight="duotone" />
              Find your local
            </a>
          </motion.div>

          <motion.p
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={6}
            className="mt-5 text-sm text-zinc-600 flex items-center gap-2"
            data-testid="hero-support-local"
          >
            <span className="inline-block h-1.5 w-1.5 rounded-full brand-gradient animate-pulse" />
            <span>You already know.</span>
            <span className="text-zinc-300 mx-1">·</span>
            <span className="text-zinc-500">just tap &amp; wait</span>
          </motion.p>
        </div>

        {/* Phone mockup */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.94, rotate: -8 }}
          animate={{ opacity: 1, y: 0, scale: 1, rotate: -6 }}
          transition={{ duration: 1, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5 relative hidden md:block"
          data-testid="hero-visual"
          style={{ perspective: "1600px" }}
        >
          {/* Soft ambient glow halo */}
          <div className="absolute -inset-16 brand-gradient blur-3xl opacity-30 rounded-full" />

          {/* Floating 3D phone (iPhone 16 Pro style) */}
          <motion.div
            animate={{ y: [0, -16, 0] }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="relative mx-auto w-full max-w-[290px] lg:max-w-[330px]"
            style={{
              transformStyle: "preserve-3d",
              transform: "rotateY(-14deg) rotateX(7deg) rotateZ(-2deg)",
            }}
          >
            {/* Titanium outer frame */}
            <div
              className="relative rounded-[3.2rem] p-[3px]"
              style={{
                background:
                  "linear-gradient(135deg, #d4d4d8 0%, #71717a 25%, #3f3f46 50%, #71717a 75%, #d4d4d8 100%)",
                boxShadow:
                  "0 60px 120px -30px rgba(217, 30, 94, 0.5), 0 40px 80px -20px rgba(192, 38, 211, 0.4), 0 25px 50px -15px rgba(15,15,20,0.6)",
              }}
            >
              {/* Inner titanium body */}
              <div
                className="relative rounded-[3.05rem] p-[9px]"
                style={{
                  background:
                    "linear-gradient(160deg, #18181b 0%, #27272a 50%, #18181b 100%)",
                }}
              >
                {/* Bezel highlight */}
                <span
                  aria-hidden
                  className="absolute inset-0 rounded-[3.05rem] pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0) 22%, rgba(255,255,255,0) 78%, rgba(255,255,255,0.12) 100%)",
                  }}
                />
                {/* Side buttons */}
                <span className="absolute -left-[4px] top-24 h-14 w-[3px] rounded-l-md bg-gradient-to-b from-zinc-500 via-zinc-700 to-zinc-500" />
                <span className="absolute -left-[4px] top-40 h-9 w-[3px] rounded-l-md bg-gradient-to-b from-zinc-500 via-zinc-700 to-zinc-500" />
                <span className="absolute -left-[4px] top-52 h-9 w-[3px] rounded-l-md bg-gradient-to-b from-zinc-500 via-zinc-700 to-zinc-500" />
                <span className="absolute -right-[4px] top-32 h-20 w-[3px] rounded-r-md bg-gradient-to-b from-zinc-500 via-zinc-700 to-zinc-500" />

                {/* Screen */}
                <div className="relative rounded-[2.55rem] overflow-hidden bg-white aspect-[9/19.5]">
                  {/* Dynamic Island */}
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 h-[26px] w-[100px] rounded-full bg-black flex items-center justify-end pr-2 gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-zinc-800 ring-1 ring-zinc-700" />
                  </div>
                  <img
                    src={APP_SCREENSHOT}
                    alt="My Local app home screen"
                    loading="eager"
                    className="absolute inset-0 h-full w-full object-cover object-top"
                  />
                  {/* Subtle screen glare */}
                  <span
                    aria-hidden
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(120deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 30%, rgba(255,255,255,0) 70%, rgba(255,255,255,0.10) 100%)",
                      mixBlendMode: "screen",
                    }}
                  />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Floor shadow (also drifts subtly to sell the float) */}
          <motion.div
            aria-hidden
            animate={{ scale: [1, 0.92, 1], opacity: [0.35, 0.22, 0.35] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -bottom-6 left-1/2 -translate-x-1/2 h-10 w-3/5 rounded-full bg-black blur-2xl"
          />
        </motion.div>
      </div>
    </section>
  );
}
